import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Badge,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  InputBase,
  Paper
} from '@mui/material';
import {
  Menu as MenuIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useAuth } from './context/AuthContext';
import { useCartCount } from './context/CartContext';
import { getSiteSettings, SiteSettings, initializeSiteSettings } from './services/FirestoreService';
import { addNewsletterSubscriber } from './services/AdditionalFirestoreService';

// Import components
import Homepage from './components/home/Homepage';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import Profile from './components/user/Profile';
import Cart from './components/cart/Cart';
import Checkout from './components/checkout/Checkout';
import CategoryPage from './components/category/CategoryPage';
import ProductDetail from './components/product/ProductDetail';
import BlogList from './components/blog/BlogList';
import BlogDetail from './components/blog/BlogDetail';
import AdminPanel from './components/admin/AdminPanel';
import AdminRoute from './components/admin/AdminRoute';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Contact from './components/contact/Contact';
import About from './components/about/About';

interface ResponsiveAppBarInnerProps {
  siteSettings: SiteSettings | null;
}

const ResponsiveAppBarInner: React.FC<ResponsiveAppBarInnerProps> = ({ siteSettings }) => {
  const { currentUser, userDisplayName, logout, isAdmin } = useAuth();
  const cartCount = useCartCount();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const location = useLocation();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const menuItems = [
    { text: 'Ana Sayfa', path: '/' },
    { text: 'Ürünler', path: '/products' },
    { text: 'Blog', path: '/blog' },
    { text: 'Hakkımızda', path: '/about' },
    { text: 'İletişim', path: '/contact' },
  ];

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newsletterEmail || !/^\S+@\S+\.\S+$/.test(newsletterEmail)) {
      setNewsletterMessage({ type: 'error', text: 'Lütfen geçerli bir e-posta adresi girin.' });
      return;
    }
    
    try {
      await addNewsletterSubscriber(newsletterEmail);
      setNewsletterMessage({ type: 'success', text: 'E-posta listemize başarıyla kaydoldunuz!' });
      setNewsletterEmail('');
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      setNewsletterMessage({ type: 'error', text: 'Kayıt sırasında bir hata oluştu. Lütfen tekrar deneyin.' });
    }
  };

  return (
    <div className="bg-white shadow-md">
      {/* Top bar */}
      <div className="bg-gray-100 py-2 hidden md:block">
        <Container maxWidth="xl">
          <div className="flex justify-between items-center">
            <Typography variant="body2" color="textSecondary">
              {siteSettings ? `${siteSettings.freeShippingThreshold} TL ve üzeri alışverişlerinizde ücretsiz kargo` : '750 TL ve üzeri alışverişlerinizde ücretsiz kargo'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Siparişleriniz {siteSettings ? siteSettings.orderProcessingTime : '48 saatte'} kapınıza ulaşır
            </Typography>
          </div>
        </Container>
      </div>

      {/* Main navigation */}
      <Container maxWidth="xl">
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center">
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 2, color: 'black', display: { md: 'none' } }}
              onClick={toggleMobileMenu}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              component={Link}
              to="/"
              className="text-xl font-bold text-blue-600 no-underline"
            >
              Rölyef Boyama Setleri
            </Typography>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex space-x-4">
            {menuItems.map((item) => (
              <Button
                key={item.path}
                color="inherit"
                component={Link}
                to={item.path}
                className={`text-black ${location.pathname === item.path ? 'font-bold' : ''}`}
              >
                {item.text}
              </Button>
            ))}
          </div>

          <div className="flex items-center space-x-2">
            <IconButton
              size="large"
              color="inherit"
              component={Link}
              to="/cart"
              className="text-black"
            >
              <Badge badgeContent={cartCount} color="error">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>

            {currentUser ? (
              <>
                <Button color="inherit" component={Link} to="/profile" className="text-black hidden md:block">
                  <PersonIcon sx={{ mr: 1 }} />
                  {userDisplayName || 'Profil'}
                </Button>
                {isAdmin && (
                  <Button color="inherit" component={Link} to="/admin" className="text-black hidden md:block">
                    Admin
                  </Button>
                )}
                <Button color="inherit" onClick={logout} className="text-black hidden md:block">
                  Çıkış Yap
                </Button>
                <Button color="inherit" onClick={logout} className="text-black md:hidden">
                  Çıkış
                </Button>
              </>
            ) : (
              <>
                <Button color="inherit" component={Link} to="/login" className="text-black hidden md:block">
                  Giriş
                </Button>
                <Button color="inherit" component={Link} to="/signup" className="text-black hidden md:block">
                  Kayıt
                </Button>
                <Button color="inherit" component={Link} to="/login" className="text-black md:hidden">
                  <PersonIcon />
                </Button>
              </>
            )}
          </div>
        </div>
      </Container>

      {/* Mobile menu drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
      >
        <Box sx={{ width: 250 }} role="presentation">
          <div className="flex justify-between items-center p-4 border-b">
            <Typography variant="h6">Menü</Typography>
            <IconButton onClick={toggleMobileMenu}>
              <CloseIcon />
            </IconButton>
          </div>
          <List>
            {menuItems.map((item) => (
              <ListItem
                key={item.path}
                component={Link}
                to={item.path}
                onClick={toggleMobileMenu}
                className={location.pathname === item.path ? 'bg-blue-100' : ''}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
            <ListItem
              component={Link}
              to="/cart"
              onClick={toggleMobileMenu}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemText primary={`Sepet (${cartCount})`} />
            </ListItem>
            {currentUser ? (
              <>
                <ListItem
                  component={Link}
                  to="/profile"
                  onClick={toggleMobileMenu}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemText primary="Profil" />
                </ListItem>
                {isAdmin && (
                  <ListItem
                    component={Link}
                    to="/admin"
                    onClick={toggleMobileMenu}
                    sx={{ cursor: 'pointer' }}
                >
                    <ListItemText primary="Admin" />
                  </ListItem>
                )}
                <ListItem
                  onClick={() => { logout(); toggleMobileMenu(); }}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemText primary="Çıkış" />
                </ListItem>
              </>
            ) : (
              <>
                <ListItem
                  component={Link}
                  to="/login"
                  onClick={toggleMobileMenu}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemText primary="Giriş" />
                </ListItem>
                <ListItem
                  component={Link}
                  to="/signup"
                  onClick={toggleMobileMenu}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemText primary="Kayıt" />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </div>
  );
};

interface ResponsiveAppBarProps {
  siteSettings: SiteSettings | null;
}

const ResponsiveAppBar: React.FC<ResponsiveAppBarProps> = ({ siteSettings }) => {
  return <ResponsiveAppBarInner siteSettings={siteSettings} />;
};

const App: React.FC = () => {
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    const initializeAndFetchSiteSettings = async () => {
      try {
        // Initialize site settings if they don't exist
        await initializeSiteSettings();
        
        // Then fetch the settings
        const settings = await getSiteSettings();
        setSiteSettings(settings);
      } catch (error) {
        console.error('Error initializing/fetching site settings:', error);
        // Set default values if there's an error
        setSiteSettings({
          id: 'site',
          siteTitle: 'Rölyef Boyama Setleri',
          siteDescription: 'Kaliteli ve eğlenceli boya setleri',
          logoUrl: '',
          faviconUrl: '',
          freeShippingThreshold: 750,
          orderProcessingTime: '2-3 iş günü',
          contactEmail: '',
          contactPhone: '',
          currency: 'TRY',
          currencySymbol: '₺',
          defaultLanguage: 'tr',
          analyticsId: '',
          facebookPixelId: '',
          googleTagManagerId: '',
          newsletterEnabled: true,
          googleAnalyticsId: '',
          privacyPolicy: '',
          termsOfService: '',
          refundPolicy: ''
        });
      }
    };

    initializeAndFetchSiteSettings();
  }, []);

  const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterMessage, setNewsletterMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

    const handleNewsletterSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      
      if (!newsletterEmail || !/^\S+@\S+\.\S+$/.test(newsletterEmail)) {
        setNewsletterMessage({ type: 'error', text: 'Lütfen geçerli bir e-posta adresi girin.' });
        return;
      }
      
      try {
        await addNewsletterSubscriber(newsletterEmail);
        setNewsletterMessage({ type: 'success', text: 'E-posta listemize başarıyla kaydoldunuz!' });
        setNewsletterEmail('');
      } catch (error) {
        console.error('Error subscribing to newsletter:', error);
        setNewsletterMessage({ type: 'error', text: 'Kayıt sırasında bir hata oluştu. Lütfen tekrar deneyin.' });
      }
    };

    return (
      <div className="min-h-screen bg-gray-50">
        <ResponsiveAppBar siteSettings={siteSettings} />
        <main className="py-4">
          <Container maxWidth="xl" className="px-4">
            {children}
          </Container>
        </main>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-8 mt-8">
          <Container maxWidth="xl" className="px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <Typography variant="h6" gutterBottom className="text-lg font-bold">
                  Hakkımızda
                </Typography>
                <Typography variant="body2" className="text-gray-300">
                  Türkiye'nin önde gelen boya seti satıcısıyız. Kaliteli ürünler ve hızlı hizmet anlayışımızla hizmetinizdeyiz.
                </Typography>
              </div>
              <div>
                <Typography variant="h6" gutterBottom className="text-lg font-bold">
                  Hızlı Bağlantılar
                </Typography>
                <ul className="space-y-2">
                  <li><Link to="/" className="text-gray-300 hover:text-white">Ana Sayfa</Link></li>
                  <li><Link to="/products" className="text-gray-300 hover:text-white">Ürünler</Link></li>
                  <li><Link to="/blog" className="text-gray-300 hover:text-white">Blog</Link></li>
                  <li><Link to="/about" className="text-gray-300 hover:text-white">Hakkımızda</Link></li>
                  <li><Link to="/contact" className="text-gray-300 hover:text-white">İletişim</Link></li>
                </ul>
              </div>
              <div>
                <Typography variant="h6" gutterBottom className="text-lg font-bold">
                  Müşteri Hizmetleri
                </Typography>
                <ul className="space-y-2">
                  {siteSettings?.contactPhone && (
                    <li className="text-gray-300">Telefon: {siteSettings.contactPhone}</li>
                  )}
                  {siteSettings?.contactEmail && (
                    <li className="text-gray-300">Email: {siteSettings.contactEmail}</li>
                  )}
                  <li className="text-gray-300">Adres: İstanbul, Türkiye</li>
                </ul>
              </div>
              <div>
                <Typography variant="h6" gutterBottom className="text-lg font-bold">
                  Bülten Aboneliği
                </Typography>
                {siteSettings?.newsletterEnabled !== false && (
                  <form onSubmit={handleNewsletterSubmit}>
                    <div className="flex">
                      <input
                        type="email"
                        placeholder="Email adresiniz"
                        className="px-3 py-2 bg-gray-700 text-white rounded-l w-full"
                        value={newsletterEmail}
                        onChange={(e) => setNewsletterEmail(e.target.value)}
                      />
                      <button 
                        type="submit"
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-r"
                      >
                        Abone Ol
                      </button>
                    </div>
                  </form>
                )}
                {newsletterMessage && (
                  <div className={`mt-2 text-sm ${newsletterMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                    {newsletterMessage.text}
                  </div>
                )}
              </div>
            </div>
            <div className="border-t border-gray-700 mt-8 pt-6 text-center text-gray-400">
              <Typography variant="body2">
                © {new Date().getFullYear()} Rölyef Boyama Setleri. Tüm hakları saklıdır.
              </Typography>
            </div>
          </Container>
        </footer>
      </div>
    );
  };

  return (
    <AuthProvider>
      <CartProvider>
        <WishlistProvider>
          <Router>
            <Layout>
              <Routes>
                <Route path="/" element={<Homepage />} />
                <Route path="/login" element={
                  <ProtectedRoute>
                    <Login />
                  </ProtectedRoute>
                } />
                <Route path="/signup" element={
                  <ProtectedRoute>
                    <Signup />
                  </ProtectedRoute>
                } />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/products" element={<CategoryPage />} />
                <Route path="/category/:id" element={<CategoryPage />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:id" element={<BlogDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/admin" element={
                  <AdminRoute>
                    <AdminPanel />
                  </AdminRoute>
                } />
              </Routes>
            </Layout>
          </Router>
        </WishlistProvider>
      </CartProvider>
    </AuthProvider>
  );
};

export default App;